// AI Usage dashboard — global token/cost KPIs and per-user breakdown table
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { captureException } from '@startus/lib';
import { fetchAiUsageData } from '../../services/ai-usage.service';
import type { AiUsageMetrics, AiUserUsage } from '../../types/ai-usage';
import { MetricCard } from '../../components/dashboard/MetricCard';

const formatUsd = (v: number): string => {
  if (v === 0) return '$0.00';

  if (v < 0.01) {
    const formatted = v.toFixed(6).replace(/0+$/, '');
    return `$${formatted}`;
  }

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);
};

// Dropdown multi-select to filter the table by user
function UserFilter({
  users,
  selected,
  onChange,
}: {
  users: AiUserUsage[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const { t } = useTranslation('admin');
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const toggle = (userId: string) => {
    const next = new Set(selected);
    if (next.has(userId)) next.delete(userId);
    else next.add(userId);
    onChange(next);
  };

  const label =
    selected.size === 0
      ? t('aiUsage.filter.all')
      : t('aiUsage.filter.selected', { count: selected.size });

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm transition-colors"
      >
        {/* Filter icon */}
        <svg className="w-4 h-4 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M3 4h18M6 8h12M9 12h6M11 16h2"
          />
        </svg>
        {label}
        <svg
          className={`w-3 h-3 opacity-50 transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-72 bg-[#2a2a2a] rounded-lg border border-white/10 shadow-lg z-20 max-h-64 overflow-y-auto">
          {selected.size > 0 && (
            <div className="px-3 py-2 border-b border-white/10">
              <button
                type="button"
                onClick={() => onChange(new Set())}
                className="text-xs text-[#19A2F8] hover:underline"
              >
                {t('aiUsage.filter.clearAll')}
              </button>
            </div>
          )}
          <ul className="py-1">
            {users.map((u) => (
              <li key={u.userId}>
                <label className="flex items-center gap-3 px-3 py-2 hover:bg-white/5 cursor-pointer text-sm">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm checkbox-primary"
                    checked={selected.has(u.userId)}
                    onChange={() => toggle(u.userId)}
                  />
                  <span className="truncate">{u.email}</span>
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

const AiUsagePage: React.FC = () => {
  const { t } = useTranslation(['common', 'admin']);
  const [data, setData] = useState<AiUsageMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchAiUsageData()
      .then(setData)
      .catch((err: unknown) => {
        captureException(err, { page: 'admin-ai-usage' });
        setError(true);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <span className="loading loading-spinner loading-lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div role="alert" className="alert alert-error">
        <span>{t('common.error')}</span>
      </div>
    );
  }

  const { aiUsage } = data;
  const hasUsers = aiUsage.byUser.length > 0;

  const filteredUsers =
    selectedUsers.size === 0
      ? aiUsage.byUser
      : aiUsage.byUser.filter((u) => selectedUsers.has(u.userId));

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6">
      {/* Global KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MetricCard label={t('admin:aiUsage.tokensThisMonth')} value={aiUsage.tokensMonth} />
        <MetricCard
          label={t('admin:aiUsage.costThisMonth')}
          value={formatUsd(aiUsage.costMonthUsd)}
        />
      </div>

      {/* Per-user breakdown — only rendered when data exists */}
      {hasUsers && (
        <div className="bg-[#1E1E1E] rounded-2xl p-6 space-y-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-lg font-semibold">{t('admin:aiUsage.byUser.title')}</h2>
            <UserFilter
              users={aiUsage.byUser}
              selected={selectedUsers}
              onChange={setSelectedUsers}
            />
          </div>

          <div className="overflow-x-auto">
            <table className="table w-full text-sm">
              <thead>
                <tr className="border-white/10">
                  <th className="text-white/50 font-medium bg-transparent">
                    {t('admin:aiUsage.byUser.email')}
                  </th>
                  <th className="text-white/50 font-medium text-right bg-transparent">
                    {t('admin:aiUsage.byUser.tokens')}
                  </th>
                  <th className="text-white/50 font-medium text-right bg-transparent">
                    {t('admin:aiUsage.byUser.cost')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.userId} className="border-white/10 hover:bg-white/5 transition-colors">
                    <td className="font-mono text-xs">{u.email}</td>
                    <td className="text-right tabular-nums">{u.tokensMonth.toLocaleString()}</td>
                    <td className="text-right tabular-nums text-[#19A2F8]">
                      {formatUsd(u.costMonthUsd)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiUsagePage;
